/**
 * On-Device Health Data Encryption
 * 
 * Uses Web Crypto API (AES-GCM) to encrypt sensitive health data at rest
 * on the device via Capacitor Preferences.
 * 
 * Key derivation uses PBKDF2 from the user's auth session token hash,
 * ensuring data is tied to the authenticated user.
 */

const ENCRYPTION_ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96-bit IV for AES-GCM
const SALT_LENGTH = 16;
const PBKDF2_ITERATIONS = 100_000;

let cachedKey: CryptoKey | null = null;
let cachedKeySource: string | null = null;

function isSubtleCryptoAvailable(): boolean {
  return typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.subtle !== 'undefined';
}

function stringToArrayBuffer(str: string): ArrayBuffer {
  return new TextEncoder().encode(str).buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Derive an AES-GCM key from a user session token using PBKDF2
 */
async function deriveKey(sessionToken: string, salt: Uint8Array): Promise<CryptoKey> {
  const tokenBuffer = new TextEncoder().encode(sessionToken);
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    tokenBuffer as unknown as ArrayBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as unknown as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: ENCRYPTION_ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Get or derive the encryption key for the current session
 */
async function getEncryptionKey(sessionToken: string, salt: Uint8Array): Promise<CryptoKey> {
  if (cachedKey && cachedKeySource === sessionToken) {
    return cachedKey;
  }
  cachedKey = await deriveKey(sessionToken, salt);
  cachedKeySource = sessionToken;
  return cachedKey;
}

/**
 * Encrypt health data for local storage.
 * Returns a base64-encoded string containing: salt + IV + ciphertext
 */
export async function encryptHealthData(
  data: object,
  sessionToken: string
): Promise<string> {
  if (!isSubtleCryptoAvailable()) {
    // Fallback: store as-is on platforms without SubtleCrypto
    console.warn('[DeviceEncryption] SubtleCrypto not available, storing unencrypted');
    return JSON.stringify({ _unencrypted: true, data });
  }

  try {
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const key = await getEncryptionKey(sessionToken, salt);

    const plaintext = new TextEncoder().encode(JSON.stringify(data));
    const ciphertext = await crypto.subtle.encrypt(
      { name: ENCRYPTION_ALGORITHM, iv },
      key,
      plaintext
    );

    // Combine: salt (16) + iv (12) + ciphertext
    const combined = new Uint8Array(salt.length + iv.length + new Uint8Array(ciphertext).length);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(ciphertext), salt.length + iv.length);

    return arrayBufferToBase64(combined.buffer);
  } catch (error) {
    console.error('[DeviceEncryption] Encryption failed:', error);
    throw new Error('Failed to encrypt health data');
  }
}

/**
 * Decrypt health data from local storage.
 * Accepts the base64-encoded string produced by encryptHealthData.
 */
export async function decryptHealthData(
  encrypted: string,
  sessionToken: string
): Promise<object> {
  // Handle unencrypted fallback data
  try {
    const parsed = JSON.parse(encrypted);
    if (parsed._unencrypted) {
      return parsed.data;
    }
  } catch {
    // Not JSON, proceed with decryption
  }

  if (!isSubtleCryptoAvailable()) {
    throw new Error('SubtleCrypto not available for decryption');
  }

  try {
    const combined = new Uint8Array(base64ToArrayBuffer(encrypted));

    const salt = combined.slice(0, SALT_LENGTH);
    const iv = combined.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const ciphertext = combined.slice(SALT_LENGTH + IV_LENGTH);

    const key = await getEncryptionKey(sessionToken, salt);

    const plaintext = await crypto.subtle.decrypt(
      { name: ENCRYPTION_ALGORITHM, iv },
      key,
      ciphertext
    );

    return JSON.parse(new TextDecoder().decode(plaintext));
  } catch (error) {
    console.error('[DeviceEncryption] Decryption failed:', error);
    throw new Error('Failed to decrypt health data. Session may have changed.');
  }
}

/**
 * Clear cached encryption key (call on logout)
 */
export function clearEncryptionCache(): void {
  cachedKey = null;
  cachedKeySource = null;
}
